import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSessionRecovery } from '@/stores/sessionRecovery';
import { useSessionTransition } from '@/stores/sessionTransition';
import { followCookieSession, leaveRecoveryForLogin, probeSessionRecovery, restoreLocalSession } from '@/lib/session-recovery';
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/v2/design-system/primitives';
import { landingFor } from '@/v2/utils/landing';

export function SessionRecoveryDialog() {
  const recovery = useSessionRecovery();
  const switching = useSessionTransition((state) => state.isSwitchingTenant);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmTenant, setConfirmTenant] = useState<string | null>(null);
  useEffect(() => {
    if (recovery.phase === 'probing') void probeSessionRecovery(queryClient);
  }, [recovery.generation, recovery.phase, queryClient]);
  useEffect(() => { setConfirmTenant(null); }, [recovery.phase, recovery.generation]);

  const localName = user?.clinics?.find((clinic) => clinic.id === recovery.owner?.tenantId)?.name ?? 'esta clínica';
  const activeName = user?.clinics?.find((clinic) => clinic.id === recovery.observedTenantId)?.name ?? 'a clínica ativa';
  const changing = switching && recovery.phase === 'idle';
  const busy = changing || ['probing', 'restoring', 'following'].includes(recovery.phase);
  const preventClose = (event: Event) => event.preventDefault();

  return (
    <Dialog open={recovery.phase !== 'idle' || switching}>
      <DialogContent showClose={false} overlayClassName="z-[1000]" className="z-[1001]" data-ui="v2"
        onEscapeKeyDown={preventClose} onPointerDownOutside={preventClose} onInteractOutside={preventClose}>
        <DialogHeader>
          <DialogTitle>{changing ? 'Confirmando sua sessão' : recovery.phase === 'login-required' ? 'Confirme seu acesso' : 'A clínica ativa mudou em outra aba'}</DialogTitle>
          <DialogDescription>Sua tela permanece aberta. Nenhuma operação será reenviada automaticamente.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {busy && <p role="status">{changing ? 'Alterando clínica…' : recovery.phase === 'probing' ? 'Verificando sua sessão…' : 'Confirmando a clínica…'}</p>}
          {recovery.phase === 'choice' && (confirmTenant ? (
            <p>Ao acompanhar {activeName}, os formulários abertos nesta tela serão fechados e seus campos não salvos serão perdidos. Rascunhos de atendimento e conversa serão guardados nesta aba para quando você voltar à clínica original.</p>
          ) : (
            <>
              <p>Você estava trabalhando em <strong>{localName}</strong>. Outra aba ativou <strong>{activeName}</strong>.</p>
              <p>Continuar nesta clínica mantém seus formulários preenchidos. As abas compartilham a clínica ativa; essa escolha pode pausar a outra aba.</p>
              {recovery.restoreDenied && <p role="alert">Seu acesso a esta clínica não está mais disponível. Você pode acompanhar a clínica ativa.</p>}
            </>
          ))}
          {recovery.phase === 'offline' && <p>Não foi possível confirmar sua sessão agora. Sua tela continua preservada. Verifique a conexão e tente novamente.</p>}
          {recovery.phase === 'login-required' && <p>A sessão expirou ou outra conta foi acessada neste navegador. Entre novamente para confirmar sua identidade. Ao sair desta tela, campos de formulários não salvos serão perdidos. Rascunhos de atendimento e conversa ficam guardados somente nesta aba para a mesma conta e clínica. Entrar com outra conta apaga esses rascunhos.</p>}
        </DialogBody>
        {!busy && <DialogFooter className="flex-col items-stretch sm:flex-row">
          {recovery.phase === 'choice' && (confirmTenant ? <>
            <Button variant="outline" onClick={() => setConfirmTenant(null)}>Voltar e manter formulário</Button>
            <Button onClick={() => { void followCookieSession(queryClient, confirmTenant, (confirmed) => navigate(landingFor(confirmed.role), { replace: true })); }}>Confirmar troca e fechar formulários</Button>
          </> : <>
            <Button variant="outline" onClick={() => setConfirmTenant(recovery.observedTenantId)}>Acompanhar a clínica ativa</Button>
            {!recovery.restoreDenied && <Button onClick={() => { void restoreLocalSession(queryClient); }}>Continuar nesta clínica</Button>}
          </>)}
          {recovery.phase === 'offline' && <Button onClick={() => { void probeSessionRecovery(queryClient); }}>Tentar novamente</Button>}
          {recovery.phase === 'login-required' && <Button onClick={() => { if (leaveRecoveryForLogin(queryClient)) navigate('/login', { replace: true }); }}>Ir para login e fechar formulários</Button>}
        </DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
